
import NavigationButtons from '@/components/NavigationButtons';
import { Head } from 'vite-react-ssg';
import { Link } from 'react-router-dom';
import RelatedLinks from '@/components/RelatedLinks';

const About = () => {
  return (
    <>
      <Head>
        <title>About DeepSeek AI Fan Hub — Who We Are (2026)</title>
        <meta name="description" content="Independent DeepSeek AI fan hub. Learn who runs the site, why we cover DeepSeek V4, R1 and the API, and how we stay unaffiliated with deepseek.com." />
        <link rel="canonical" href="https://deepseek.ai/about" />
        
        <meta property="og:title" content="About DeepSeek AI Fan Hub — Independent 2026 Community" />
        <meta property="og:description" content="Meet the independent team behind the DeepSeek AI fan hub: why we cover DeepSeek V4, R1 and the API, our editorial standards and how we stay unaffiliated from DeepSeek.com." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://deepseek.ai/about" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About DeepSeek AI Fan Hub — Independent 2026 Community" />
        <meta name="twitter:description" content="Independent fan hub covering DeepSeek V4, R1 and the API. Learn who we are, our standards and our non-affiliation with DeepSeek.com." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "The Independent DeepSeek Guide",
            "url": "https://deepseek.ai",
            "description": "An independent fan site dedicated to sharing information about DeepSeek.com's AI technology and innovations."
          })}
        </script>
      </Head>

      <div className="min-h-screen pt-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-deepseek-secondary mb-8">About This DeepSeek Fan Site</h1>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Important:</strong> This website is an independent fan site created by enthusiasts of DeepSeek.com. 
              We are not affiliated with, sponsored by, or endorsed by Hangzhou DeepSeek Artificial Intelligence Co., Ltd.
              For official information, please visit <a href="https://www.deepseek.com" target="_blank" rel="noopener noreferrer" className="underline">DeepSeek.com</a>.
            </p>
          </div>
          
          <NavigationButtons />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-deepseek-primary">About DeepSeek.com</h2>
              <p className="text-lg text-gray-600">
                DeepSeek.com is developed by Hangzhou DeepSeek Artificial Intelligence Co., Ltd., a company dedicated to advancing artificial intelligence research and development. They create cutting-edge AI models and solutions that push the boundaries of what's possible in machine learning.
              </p>
              <p className="text-lg text-gray-600">
                DeepSeek is known for their innovative language models, including their flagship reasoning models and coding assistants. Their technology combines powerful neural architectures with efficient training methodologies to deliver state-of-the-art AI capabilities.
              </p>
              <h2 className="text-2xl font-semibold text-deepseek-primary pt-4">Why We're Fans</h2>
              <p className="text-lg text-gray-600">
                We're enthusiastic about DeepSeek's commitment to advancing AI research and making powerful AI tools accessible. Their open approach to sharing research and developing practical AI solutions aligns with our belief in the democratization of AI technology.
              </p>
              <p className="text-lg text-gray-600">
                From their breakthrough <Link to="/deepseek-r1" className="text-blue-600 hover:underline">DeepSeek R1</Link> reasoning model to their upcoming 
                <Link to="/blog/deepseek-r2-challenges" className="text-blue-600 hover:underline"> R2 release</Link>, we're excited to share information about their latest innovations.
              </p>
            </div>
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-deepseek-primary">Our Mission as Fans</h2>
              <p className="text-lg text-gray-600">
                As enthusiasts of DeepSeek.com, our mission is to share information and insights about their groundbreaking AI technology. We aim to help others understand and appreciate the innovations coming from the DeepSeek team.
              </p>
              <p className="text-lg text-gray-600">
                We believe in the power of AI to transform industries and improve lives, and we're excited to showcase how DeepSeek is contributing to this transformation through their research and development efforts.
              </p>
              
              <h2 className="text-2xl font-semibold text-deepseek-primary pt-4">What We Cover</h2>
              <ul className="space-y-4 text-lg text-gray-600">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Research Updates:</span> Latest developments in <Link to="/blog/what-is-deep-learning-ai" className="text-blue-600 hover:underline">deep learning</Link> and AI from DeepSeek
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Model Analysis:</span> In-depth looks at DeepSeek's AI models and their capabilities
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Industry Impact:</span> How DeepSeek's innovations are shaping the AI landscape
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">Educational Content:</span> Helping others understand AI concepts and DeepSeek's contributions
                </li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-deepseek-primary pt-6">About Our Team</h2>
              <p className="text-lg text-gray-600">
                We are a group of AI enthusiasts, researchers, and developers who are passionate about sharing knowledge about DeepSeek's innovations. Our team includes individuals with backgrounds in machine learning, software development, and AI research.
              </p>
              <p className="text-lg text-gray-600">
                While we're not affiliated with DeepSeek.com, we're committed to providing accurate, informative content about their technology and its impact on the AI field.
              </p>
              
              <h2 className="text-2xl font-semibold text-deepseek-primary pt-6">Disclaimer</h2>
              <p className="text-lg text-gray-600">
                This website is created and maintained by independent fans and enthusiasts of DeepSeek.com. We provide information, resources, and updates about DeepSeek's innovations purely as admirers of their work. For official information, please visit <a href="https://www.deepseek.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">DeepSeek.com</a>.
              </p>
            </div>
          </div>
          
          <div className="mt-16">
            <h2 className="text-2xl font-semibold text-deepseek-primary mb-6">Learn More</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <RelatedLinks />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
